import React, { useState, useEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './SortableSection.css';

interface SortableSectionProps {
  id: string;
  full: boolean;
  label: string;
  editMode: boolean;
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  /** No mobile, sempre inicia colapsado e não persiste o estado aberto */
  alwaysCollapsedOnMobile?: boolean;
  /**
   * No mobile, inicia ABERTO até ela fechar — e aí respeita (23/10).
   *
   * Até 23/10 o fechado nunca era gravado: "eu ocultei nova transação mas
   * sempre que abro o app ele tá aberto". O motivo da trava (editar rolava
   * até um botão fechado) acabou quando a edição no celular virou modal
   * (`EdicaoModal`). A escolha do celular mora numa chave SÓ dele, então o
   * desktop continua sem vazar para cá, nem daqui para lá.
   *
   * O espelho do de cima, e existe porque `sec-form` estava do lado errado.
   * Colapsado, o mobile mostrava um botão "Nova Transação" no lugar do card —
   * então o campo único ficava escondido atrás de um toque, e editar um
   * lançamento rolava até um botão fechado em vez de abrir o formulário.
   *
   * Não basta remover o `alwaysCollapsedOnMobile`: sem ele o estado cai no
   * localStorage, e um `true` gravado numa sessão de desktop reabriria o
   * mesmo problema no celular sem nada explicar.
   */
  alwaysExpandedOnMobile?: boolean;
  onToggleFull?: () => void;
}

const isMobile = () => window.innerWidth <= 1024;

const EVENTO_ABRIR_SECAO = 'wallet:abrir-secao';

/** Abre a seção se estiver fechada, sem gravar isso como escolha dela. */
export const abrirSecao = (id: string): void => {
  window.dispatchEvent(new CustomEvent(EVENTO_ABRIR_SECAO, { detail: id }));
};

const SortableSection: React.FC<SortableSectionProps> = ({
  id,
  full,
  label,
  editMode,
  children,
  defaultCollapsed = false,
  alwaysCollapsedOnMobile = false,
  alwaysExpandedOnMobile = false,
  onToggleFull,
}) => {
  const storageKey = `wallet_section_collapsed_${id}`;
  /** A escolha feita no celular, separada da do desktop. */
  const mobileKey = `${storageKey}_mobile`;
  const ler = (k: string): string | null => {
    try { return localStorage.getItem(k); } catch { return null; }
  };
  const [collapsed, setCollapsed] = useState(() => {
    if (alwaysCollapsedOnMobile && isMobile()) return true;
    if (alwaysExpandedOnMobile && isMobile()) return ler(mobileKey) === 'true';
    const saved = ler(storageKey);
    return saved !== null ? saved === 'true' : defaultCollapsed;
  });

  /** Aberto a pedido do app (`abrirSecao`): mostra, mas não vira preferência. */
  const abertoAPedido = useRef(false);

  useEffect(() => {
    if (abertoAPedido.current) { abertoAPedido.current = false; return; }
    if (alwaysCollapsedOnMobile && isMobile()) return;
    // No celular, a chave do celular: a escolha daqui não vaza para o desktop.
    const k = alwaysExpandedOnMobile && isMobile() ? mobileKey : storageKey;
    try { localStorage.setItem(k, String(collapsed)); } catch { /* sem armazenamento */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed]);

  // "Lançar" no Comece por aqui rola até a seção: fechada, ela chegaria num
  // cabeçalho em vez do formulário.
  useEffect(() => {
    const abrir = (e: Event) => {
      if ((e as CustomEvent<string>).detail !== id) return;
      setCollapsed(c => {
        if (c) abertoAPedido.current = true;
        return false;
      });
    };
    window.addEventListener(EVENTO_ABRIR_SECAO, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR_SECAO, abrir);
  }, [id]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !editMode });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Quando colapsado no desktop, ocupa as 2 colunas
    gridColumn: (full || (!isMobile() && collapsed)) ? '1 / -1' : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      id={id}
      style={style}
      className={[
        'ss-wrapper',
        editMode   ? 'ss-edit'     : '',
        isDragging ? 'ss-dragging' : '',
        full       ? 'ss-full'     : '',
        collapsed  ? 'ss-collapsed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {editMode && (
        <div className="ss-handle" {...attributes} {...listeners}>
          <span className="ss-handle-dots">⠿</span>
          <span className="ss-handle-label">{label}</span>
          <button
            className={`ss-icon-btn${full ? ' ss-icon-btn-on' : ''}`}
            title={full ? 'Reduzir para meia largura' : 'Expandir para largura total'}
            onPointerDown={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); onToggleFull?.(); }}
          >
            {full ? (
              <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
                <rect x="1" y="1" width="7" height="12" rx="1.5" fill="currentColor" opacity="0.9"/>
                <rect x="10" y="1" width="7" height="12" rx="1.5" fill="currentColor" opacity="0.4"/>
              </svg>
            ) : (
              <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
                <rect x="1" y="1" width="16" height="12" rx="1.5" fill="currentColor" opacity="0.9"/>
              </svg>
            )}
          </button>
        </div>
      )}

      {/* Collapse header — visível apenas no mobile (via CSS) */}
      {!editMode && (
        <button
          className="ss-collapse-header"
          onClick={() => setCollapsed(v => !v)}
          aria-expanded={!collapsed}
        >
          <span className="ss-collapse-label">{label}</span>
          <svg
            className={`ss-collapse-chevron${collapsed ? ' ss-collapse-chevron-closed' : ''}`}
            width="16" height="16" viewBox="0 0 16 16" fill="none"
          >
            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}

      <div className={`ss-content${collapsed ? ' ss-content-hidden' : ''}`}>
        {children}
      </div>
    </div>
  );
};

export default SortableSection;
